import type { LevelSourceSetting, SmarticoSettings } from "@/lib/content/types";

/* Smartico — the gamification provider behind the kit's two sidebar widgets.

   The public API has no missions, no XP and no levels: in this stack those come
   from Smartico, which the whitelabel loads into the browser as `_smartico` and
   which the platform already talks to server-side per site. Nothing about that
   is reachable from the 86 public paths, so these three values are the only way
   in, and they are operator settings rather than code — which is why they live
   in the content store and are edited at `/admin`, the way the sportsbook's
   renderer settings are.

   They are not secrets. Smartico calls them the *public* label and brand API
   keys, and every site running the integration renders them into its own HTML.
   But a label only answers for origins Smartico has been told about, so filling
   them in is not enough on its own: the origin has to be allowlisted on the
   Smartico account too, which is somebody's decision to make.
   docs/findings/can-northstar-use-flipcoins-smartico.md

   Blank — the shipped default — every Smartico surface stays dark and the
   widgets fall back to what the platform can answer. */

export type SmarticoConfig = {
  scriptUrl: string;
  labelKey: string;
  brandKey: string;
};

/* The loader URL becomes a `<script src>`, so an operator who can edit it can
   run whatever they like on every page of the site. `/admin` is a POC password
   gate, not an identity system, and a compromise of it already reaches further
   than it should — docs/findings/admin-compromise-escalates-to-arbitrary-javascript-against-every.md.
   Pinning the host is what keeps this field from being one more way in: the key
   fields stay free text because a wrong key fails to init and nothing else. */
const SMARTICO_SCRIPT_HOSTS = [
  /* Smartico serves the loader from a CloudFront distribution rather than from
     a domain of their own, which is why this is an exact host and not a suffix:
     `*.cloudfront.net` is not a pin at all, since anyone can have one. The cost
     is that a distribution change needs an edit here rather than in /admin —
     the right way round for a field that becomes a script src. */
  "d312ucx3huj7iy.cloudfront.net",
  /* Their own domains, for the environments that use them. */
  "smartico.ai",
  "smr.vc",
];

/* An exact match, or a subdomain of one of the vendor's own domains. A
   CloudFront host is matched exactly and nothing else: `evil.cloudfront.net`
   must not pass because the real loader happens to live next door. */
const VENDOR_DOMAINS = ["smartico.ai", "smr.vc"];

const isSmarticoHost = (url: URL) =>
  SMARTICO_SCRIPT_HOSTS.includes(url.hostname) ||
  VENDOR_DOMAINS.some((domain) => url.hostname.endsWith(`.${domain}`));

export const isSmarticoScriptUrl = (value: string) => {
  try {
    const url = new URL(value);

    return url.protocol === "https:" && isSmarticoHost(url);
  } catch {
    return false;
  }
};

/* Which ladder the rail's level widget draws.

   The whitelabel makes this an explicit per-site setting (`levelComponent
   .dataSource`) rather than inferring it, so picking the platform's tiers is a
   decision an operator states, not a side effect of leaving a key blank. Both
   routes still end in the same widget.

   Two ways to land on the platform's tiers: choosing them, or having no
   working Smartico label — a site with nothing configured cannot draw Smartico
   levels, whatever the setting says. */
export const levelSourceFor = ({
  setting,
  smarticoEnabled,
}: {
  setting: LevelSourceSetting | undefined;
  smarticoEnabled: boolean;
}): "smartico" | "internalTier" =>
  setting === "Internal tiers" || !smarticoEnabled ? "internalTier" : "smartico";

/* Resolved on the server and handed to the shell as a prop, alongside the rest
   of the content.

   `wanted` is whether anything on the site would use it. The two widgets are the
   only consumers, so with both switched off in `/admin` there is nothing for
   Smartico to feed — and loading a third-party tracker onto every page to serve
   nothing is not a neutral act. Turning both flags off is the off switch for the
   vendor, not just for the widgets. */
export const smarticoConfig = (
  settings: SmarticoSettings | undefined,
  wanted = true,
): SmarticoConfig | null => {
  if (!wanted) {
    return null;
  }

  const scriptUrl = settings?.scriptUrl.trim() ?? "";
  const labelKey = settings?.labelKey.trim() ?? "";
  const brandKey = settings?.brandKey.trim() ?? "";

  /* All three or nothing. A half-filled integration loads a script that then
     fails to init, which is worse than not loading it: the tracker is on the
     page, the widgets still have no data, and the only sign is a console
     error. */
  if (!scriptUrl || !labelKey || !brandKey) {
    return null;
  }

  if (!isSmarticoScriptUrl(scriptUrl)) {
    return null;
  }

  return { scriptUrl, labelKey, brandKey };
};
